/**
 * /forgot-password — request a Supabase password-reset email.
 *
 * RC 1.35.F2 Stage-1 P0-5.
 *
 * Flow:
 *   1. User enters email → click "Send reset link".
 *   2. We call `supabase.auth.resetPasswordForEmail(email, { redirectTo })`.
 *   3. Supabase emails the user a magic recovery link of the form
 *        `${redirectTo}#access_token=...&type=recovery`.
 *   4. Clicking that link lands the user on `/auth/reset` (see
 *      ResetPasswordPage.js) where they set a new password.
 *
 * Security: we ALWAYS show the same success message regardless of
 * whether the email exists (no account-enumeration leak).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, MailCheck, ArrowLeft } from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';
import LegalFooter from '@/components/LegalFooter';

export default function ForgotPasswordPage() {
  const [email,    setEmail]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [sent,     setSent]     = useState(false);
  const [error,    setError]    = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError('Please enter your email.');
      return;
    }
    setLoading(true);
    try {
      // RedirectTo the recovery landing route. Supabase appends the
      // recovery token to the URL fragment.
      const redirectTo = `${window.location.origin}/auth/reset`;
      const { error: err } = await supabase.auth.resetPasswordForEmail(
        email.trim(),
        { redirectTo },
      );
      // Intentionally do NOT surface the err — that would leak whether
      // the email exists. We always show the same success state.
      if (err && process.env.NODE_ENV !== 'production') {
        // Dev-only echo for debugging.
        // eslint-disable-next-line no-console
        console.warn('resetPasswordForEmail error (suppressed in UI):', err.message);
      }
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-ndor-canvas" data-testid="forgot-password-page">
      <div className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-md space-y-8">
          <div className="flex flex-col items-center gap-3 text-center">
            <BrandLogo variant="symbol" size={64} />
            <p className="text-3xl font-serif-display font-semibold text-ndor-ink tracking-tight">
              NDOR<span className="text-gold-accent">Flow</span>
            </p>
          </div>

          {sent ? (
            <div
              className="rounded-xl border border-ndor-edge bg-ndor-surface p-6 space-y-3 text-center"
              data-testid="forgot-password-sent"
            >
              <MailCheck className="h-8 w-8 mx-auto text-ndor-gold" />
              <h1 className="text-xl font-serif-display font-semibold text-ndor-ink">
                Check your inbox
              </h1>
              <p className="text-sm text-ndor-ink-secondary leading-relaxed">
                If an NDORFlow account exists for <strong>{email.trim()}</strong>,
                we've emailed you a link to reset your password. The link expires
                in 60 minutes.
              </p>
              <p className="text-xs text-ndor-ink-muted">
                Didn't receive it? Check your spam folder, or try again with
                another email.
              </p>
              <Button asChild variant="outline" className="mt-4" data-testid="forgot-password-back-to-login">
                <Link to="/login">
                  <ArrowLeft className="h-4 w-4 mr-1.5" />
                  Back to sign in
                </Link>
              </Button>
            </div>
          ) : (
            <div className="rounded-xl border border-ndor-edge bg-ndor-surface p-6 space-y-5">
              <div>
                <h1 className="text-xl font-serif-display font-semibold text-ndor-ink">
                  Reset your password
                </h1>
                <p className="text-sm text-ndor-ink-secondary mt-1">
                  Enter the email you signed up with. We'll send you a secure
                  link to set a new password.
                </p>
              </div>

              {error && (
                <div
                  className="rounded-lg bg-ndor-error-light border border-ndor-error-border p-3 text-sm text-ndor-error"
                  data-testid="forgot-password-error"
                >
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email" className="text-ndor-ink">Email</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11"
                    data-testid="forgot-password-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 font-medium"
                  disabled={loading}
                  data-testid="forgot-password-submit"
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  {loading ? 'Sending…' : 'Send reset link'}
                </Button>
              </form>

              <p className="text-center text-sm text-ndor-ink-secondary">
                Remembered your password?{' '}
                <Link to="/login" className="font-medium text-ndor-ink hover:underline" data-testid="forgot-password-login-link">
                  Sign in
                </Link>
              </p>

              {/* RC 1.35.F3 follow-up — support-driven email recovery.
                  No self-serve forgot-email flow yet; pre-launch user
                  base is small enough that manual support is sufficient. */}
              <p
                className="text-center text-xs text-ndor-ink-muted pt-1"
                data-testid="forgot-email-hint"
              >
                Forgot which email you used? Contact{' '}
                <a
                  href="mailto:support@ndor.app?subject=NDORFlow%20-%20Forgot%20account%20email"
                  className="text-ndor-info hover:text-ndor-primary-hover underline"
                  data-testid="forgot-email-support-link"
                >
                  support@ndor.app
                </a>{' '}
                and mention NDORFlow.
              </p>
            </div>
          )}
        </div>
      </div>
      <LegalFooter variant="auth" />
    </div>
  );
}
